import dynamic from 'next/dynamic';
import { useState, useEffect, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';

import { useProductSheet, useSunnei } from 'context/product-sheet';
import { SunneiPicker } from 'components/sunnei/sunneiPicker';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { orderMobileImages } from 'utils/product';

import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import type { TView } from 'types/product';
import { Button as RACButton } from 'react-aria-components';
import { Button } from 'components/ui/button';
import { Dialog, DialogRoot, DialogX } from 'components/ui/dialog';
import { LabChevronLeftIcon } from 'components/camperlab/icons/chevron-left';
import { LabChevronRightIcon } from 'components/camperlab/icons/chevron-right';

const ProgressiveBlurredImg = dynamic(() => import('components/base/image'));

const Mobile3D = dynamic(() => import('components/viewer-3D/mobile3D').then((mod) => mod.Mobile3D), {
  ssr: false,
});

function Image({
  imgSrc,
  alt,
  grid,
  onClick,
  index,
  fit = 'contain',
  theme,
}: {
  imgSrc: TView;
  alt: string;
  grid?: object;
  onClick?: (src: TView, index: number) => void;
  index: number;
  fit?: 'contain' | 'cover';
  theme?: 'slide' | 'thumb' | 'gallery-zoom';
}) {
  const { isVideo } = imgSrc;

  const containClassName = {
    contain: '[&>img]:object-contain',
    cover: '[&>img]:object-cover',
  }[fit];

  const themeClassName = {
    slide: '[&>img]:object-bottom [&>img]:object-contain lg:[&>img]:min-h-[600px] [&>img]:w-full [&>img]:h-[var(--slide-height)] lg:[&>img]:h-app',
    thumb: 'lg:aspect-[2/3] [&>img]:aspect-[2/3] lg:[&>img]:aspect-[2/3]',
    'gallery-zoom': '[&>img]:h-full [&>img]:object-contain',
  }[theme];

  return (
    <div className="flex w-full" onClick={onClick ? () => onClick(imgSrc, index) : null} {...grid}>
      {isVideo ?
        <div className="flex items-start justify-center">
          <video
            className="lazy"
            // autoPlay
            loop
            title={`Video of ${alt}`}
            muted
            playsInline
            poster={imgSrc?.poster}
            dangerouslySetInnerHTML={{ __html: imgSrc?.sources }}
          />
        </div>
      : <ProgressiveBlurredImg
          //lowQualitySrc={imgSrc.xs}
          highQualitySrc={imgSrc as any}
          alt={alt}
          //sizes="(min-width: 992px) 33vw, (min-width: 768px) 70vw, 50vw"
          index={index}
          initLoaded={true}
          pictureClassName={`${containClassName} ${themeClassName ?? ''}`}
        />
      }
    </div>
  );
}

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

export const useEmblaControls = (emblaApi: EmblaCarouselType | undefined): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export function PdpImageCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const { selectedIndex, scrollSnaps } = useEmblaControls(emblaApi);
  const { productSheet } = useProductSheet();
  const { hasSunneiConfig } = useSunnei();
  const [zoomImg, setZoomImg] = useState(null);

  // estado derivado
  const defaultAlt = `${productSheet.code} - ${productSheet.brand}`;
  let images = productSheet.images ?? [];
  let galleryImages = productSheet.images ?? [];
  let imagesMobile = productSheet.imagesMobile ?? [];
  const hasMobileImages = Array.isArray(productSheet.imagesMobile) && productSheet.imagesMobile.length > 0;
  if (imagesMobile.length === 0) {
    imagesMobile = images;
  }
  // tener en cuenta sunnei
  if (hasSunneiConfig) {
    const sunneiImages = productSheet?.sunneiInfo?.products?.[productSheet?.sunneiInfo?.shoeOne]?.images;
    const sunneiImagesMobile = productSheet?.sunneiInfo?.products?.[productSheet?.sunneiInfo?.shoeOne]?.imagesMobile;
    images = sunneiImages ?? [];
    imagesMobile = sunneiImagesMobile ?? [];
    if (imagesMobile.length === 0) {
      imagesMobile = images;
    }
    galleryImages = sunneiImages ?? [];
  }

  const zoomGalleryImages = galleryImages.filter((image) => !image?.isVideo);

  const handleClickImage = (imageSrc) => {
    const foundIndex = zoomGalleryImages.findIndex((item) => item.full === imageSrc.full);
    if (foundIndex !== -1) {
      setZoomImg(foundIndex);
      dataLayerHandleEvent({
        event: 'item_photos',
        event_category: 'product detail',
        event_detail_1: 'view',
        event_detail_2: imageSrc?.md,
      });
    }
  };

  const handleCloseZoom = () => {
    setZoomImg(null);
  };

  /**
   * Antes se calculaba en servidor el orden de las imagenes si se detectaba un dispositivo movil. El problema era que se cacheaba y generaba problemas en el que el orden no es el correcto en la vista desktop o movil. Aparte veiamos un problema de hidratación de react.
   * Podemos reordernar las imágenes en el componente porque sabemos que el carousel solo se va a mostrar en movil y se esconde en desktop.
   * Ahora sólo hora se utiliza el normalizeProduct aquí.
   */
  const ImageComponents = orderMobileImages(imagesMobile).reduce((acc, imgSrc, index) => {
    if (index === 0 && hasSunneiConfig) {
      acc.push(
        <div className="w-full min-w-0 shrink-0 grow-0 basis-[var(--slide-size)] pl-[var(--slide-gap)] [&>*]:h-[var(--slide-height)] [&>*]:w-full" key={index}>
          <SunneiPicker key="sunneiPicker" />
        </div>,
      );
    }
    if (index === 1 && productSheet.viewer3d) {
      acc.push(
        <div className="w-full min-w-0 shrink-0 grow-0 basis-[var(--slide-size)] pl-[var(--slide-gap)] [&>*]:h-[var(--slide-height)] [&>*]:w-full" key={index}>
          <Mobile3D key="mobileThreeD" code={productSheet.code} mobile={true} />
        </div>,
      );
    }

    acc.push(
      <div className="w-full min-w-0 shrink-0 grow-0 basis-[var(--slide-size)] pl-[var(--slide-gap)] [&>*]:h-[var(--slide-height)] [&>*]:w-full" key={index}>
        <Image key={`ig-${index}`} onClick={() => handleClickImage(imgSrc)} imgSrc={imgSrc} alt={imgSrc?.altText || defaultAlt} index={index} theme="slide" />
      </div>,
    );
    return acc;
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <>
      <div className="flex h-auto flex-col lg:hidden">
        <div
          className="relative w-full overflow-hidden"
          style={{
            '--slide-height': 'calc(100svh - var(--ribbon-height) - var(--tnm-height) - var(--product-header-height) - var(--add-to-cart-height) - 12px)', // 90px logo, 8px product header margins, 1px dots
            '--slide-size': '100%',
            '--slide-gap': 0,
          }}
        >
          <div className="relative h-[var(--slide-height)] overflow-hidden" ref={emblaRef}>
            <div className="-ml-[var(--slide-gap)] flex h-[var(--slide-height)] touch-pan-y touch-pinch-zoom">{ImageComponents}</div>
          </div>
          <Button
            variant="none"
            className="absolute left-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-neutral/40 backdrop-blur-sm"
            onPress={scrollPrev}
          >
            <LabChevronLeftIcon className="size-5" />
            <span className="sr-only">Previous</span>
          </Button>
          <Button
            variant="none"
            className="absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-neutral/40 backdrop-blur-sm"
            onPress={scrollNext}
          >
            <LabChevronRightIcon className="size-5" />
            <span className="sr-only">Next</span>
          </Button>
        </div>
        <div className="mt-1 flex gap-0.5 px-2.5" data-dots>
          {scrollSnaps.map((_, index) => (
            <span key={index} data-selected={index === selectedIndex} className={'flex h-px w-[9px] bg-subtle data-[selected=true]:bg-neutral-inverted'} />
          ))}
        </div>
      </div>
      <div key="imageMap" className="max-lg:hidden">
        <DesktopImages images={images} onImageClick={(src) => handleClickImage(src)} defaultAlt={defaultAlt} />
      </div>
      <GalleryZoom zoomImage={zoomImg} images={zoomGalleryImages} onClose={handleCloseZoom} />

      {/* <ModalZoomImg key={zoomImg} images={zoomGalleryImages} initIndex={zoomImg} handleCloseZoom={handleCloseZoom} /> */}
    </>
  );
}

type DesktopImagesProps = {
  images: TView[];
  onImageClick: (src: TView) => void;
  defaultAlt?: string;
};
function DesktopImages({ images, onImageClick, defaultAlt }: DesktopImagesProps) {
  const [indexInView, setIndexInView] = useState(0);
  const onViewChange = useCallback((index: number) => setIndexInView(index), []);

  const onMiniaturePress = useCallback((index: number) => {
    const element = document.querySelector(`#desktopImages [data-index="${index}"]`);
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  return (
    <div className="grid grid-cols-9 items-start gap-2.5">
      <div className="sticky top-header-sticky col-span-1">
        <div className="grid max-w-[100px] gap-2.5">
          {images.map((imgSrc, index) => (
            <RACButton
              data-active={indexInView === index}
              className="rounded-none border-2 border-transparent bg-faint outline-none"
              key={imgSrc.xs}
              onPress={() => onMiniaturePress(index)}
            >
              <Image imgSrc={imgSrc} alt={imgSrc?.altText || defaultAlt} index={index} theme="thumb" />
            </RACButton>
          ))}
        </div>
      </div>
      <div id="desktopImages" className="col-span-5 col-start-3 -mt-header">
        {images.map((imgSrc, index) => (
          <ImageContainer
            indexInView={indexInView}
            key={`ig-${index}`}
            onViewChange={onViewChange}
            onClick={() => onImageClick(imgSrc)}
            img={imgSrc}
            defaultAlt={defaultAlt}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

type ImageContainerProps = {
  img: TView;
  onClick: (src: TView) => void;
  defaultAlt?: string;
  indexInView?: number;
  index?: number;
  onViewChange?: (index: number) => void;
};

function ImageContainer({ img, onClick, defaultAlt, index, indexInView, onViewChange }: ImageContainerProps) {
  const { ref, inView } = useInView({
    initialInView: index === indexInView,

    threshold: 0.75,
  });

  useEffect(() => {
    if (inView) {
      onViewChange?.(index);
    }
  }, [inView, onViewChange, index]);

  return (
    <div ref={ref} data-index={index}>
      <Image imgSrc={img} alt={img?.altText || defaultAlt} onClick={() => onClick(img)} index={index} theme="slide" />
    </div>
  );
}

type GalleryZoomProps = {
  zoomImage: number | null;
  images: TView[];
  onClose: () => void;
};
function GalleryZoom({ images, zoomImage, onClose }: GalleryZoomProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: true,
    startIndex: zoomImage ?? 0,
    align: 'start',
    inViewThreshold: 0.6,
    containScroll: 'trimSnaps',
  });
  const { selectedIndex, scrollSnaps } = useEmblaControls(emblaApi);
  const open = Number.isNaN(zoomImage) ? false : zoomImage !== null;

  return (
    <DialogRoot isDismissable isOpen={open} onOpenChange={onClose} className="relative">
      <Dialog className="h-svh w-screen max-w-none p-0">
        <div className="sticky top-0 z-10 flex justify-end">
          <DialogX />
        </div>
        <div
          className="relative -mx-2.5 h-full overflow-hidden md:hidden"
          style={{
            '--slide-height': '100%',
            '--slide-size': 'auto',
            '--slide-gap': 0,
          }}
        >
          <div className="relative h-[var(--slide-height)] overflow-hidden" ref={emblaRef}>
            <div className="-ml-[var(--slide-gap)] flex h-[var(--slide-height)] touch-pan-y touch-pinch-zoom">
              {images.map((imgSrc, index) => (
                <div
                  // className="w-full min-w-0 shrink-0 grow-0 basis-[var(--slide-size)] pl-[var(--slide-gap)] [&>*]:h-[var(--slide-height)] [&>*]:w-full"
                  className="w-auto min-w-0 shrink-0 grow-0 basis-[var(--slide-size)] pl-[var(--slide-gap)] [&>*]:h-[var(--slide-height)] [&>*]:w-auto"
                  key={index}
                >
                  <Image key={`ig-${index}`} imgSrc={imgSrc} alt={imgSrc?.altText ?? ''} index={index} theme="gallery-zoom" />
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-2.5 left-2.5 mt-1 flex gap-0.5" data-dots>
            {scrollSnaps.map((_, index) => (
              <span key={index} data-selected={index === selectedIndex} className={'flex h-px w-[9px] bg-subtle data-[selected=true]:bg-neutral-inverted'} />
            ))}
          </div>
        </div>
        <div className="flex h-full flex-col gap-2.5 max-md:hidden">
          {images.map((imgSrc, index) => (
            <div
              key={`ig-${index}`}
              ref={(el) => {
                if (index === zoomImage && el && index !== 0) {
                  el.scrollIntoView({ block: 'center', behavior: 'instant' });
                }
              }}
            >
              <Image data-index={index} imgSrc={imgSrc.full ?? (imgSrc as any)} alt={imgSrc?.altText ?? ''} index={index} theme="slide" />
            </div>
          ))}
        </div>
      </Dialog>
    </DialogRoot>
  );
}
